import { useAppDispatch } from '@app/utils/redux'

import type { ApiConsentStatus, ApiConsentType, ApiPolicy } from '@app/packages/api/v2/types/entities'
import type { UserConsentRequestItem } from '@app/packages/api/v2/types/params'
import { useThunkSelector } from '@app/packages/thunkSelector/useThunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { structuredPoliciesSelector } from '@app/store/slices/policies'

import { useEvent } from './useEvent'

type PolicyBundle = {
  policy: ApiPolicy
  consentTypes: ApiConsentType[]
}

export type SetConsentItem = { text: string; status: ApiConsentStatus; filter: (p: PolicyBundle) => boolean }

export const useSetConsent = () => {
  const dispatch = useAppDispatch()
  const policies = useThunkSelector(structuredPoliciesSelector).suspend()

  const setConsent = useEvent(async (source: string, items: SetConsentItem[]) => {
    const { postUserConsentsBulk } = await import('@app/packages/api/v2/actions/user_consents')
    const consents = items.flatMap(({ text, status, filter }) =>
      policies.filter(filter).flatMap(policy =>
        policy.consentTypes.map((ctype): UserConsentRequestItem => ({
          policy_id: policy.policy.id,
          consent_type_key: ctype.attributes.key,
          source,
          status,
          consent_method: 'button',
          consent_text: text,
        }))
      )
    )
    if (!consents.length) throw new Error('No policies found for the given consent types')
    await dispatch(callV2Api(postUserConsentsBulk({ user_consents: consents })))
  })

  return setConsent
}
